import type { MatchRng } from '../match/MatchRng';
import type { MatchState } from '../match/MatchState';
import type { PlayerRuntimeState } from '../match/PlayerRuntimeState';
import { PITCH_GEOMETRY } from '../match/PitchGeometry';
import { OffsideRule } from '../match/OffsideRule';
import { clamp, distance, type Vector2 } from '../match/Vector2';
import type { ActionCandidate, PassType } from './ActionCandidate';
import { Perception } from './Perception';
import type { PlayerDecisionContext } from './PlayerDecisionContext';
import { UtilityScorer } from './UtilityScorer';
import { ROLE_DEFINITIONS, TacticalRoleSystem, validateAssignedRole } from './TacticalRoleSystem';
import { WeightedActionSelector, type WeightedSelectionTrace } from './WeightedActionSelector';

export interface DecisionResult {
  context: PlayerDecisionContext;
  candidates: ActionCandidate[];
  chosen: ActionCandidate;
  selection: WeightedSelectionTrace;
}

export class DecisionSystem {
  constructor(
    private readonly scorer = new UtilityScorer(),
    private readonly perception = new Perception(),
    private readonly selector = new WeightedActionSelector(),
    private readonly offsides = new OffsideRule(),
    private readonly roles = new TacticalRoleSystem(),
  ) {}

  decide(state: MatchState, actor: PlayerRuntimeState, rng: MatchRng): DecisionResult {
    const context = this.context(state, actor);
    const generated = this.generateCandidates(context);
    // A genuine one-on-one / open shooting lane is not a moment in which the
    // utility selector should keep exchanging five-metre passes.  We still
    // leave normal final-third combinations available in crowded areas; this
    // only commits the actor when the goal is visibly there.
    const candidates = (this.hasClearGoalChance(context)
      ? generated.filter((candidate) => this.isFinishingCandidate(candidate, actor))
      : generated
    ).filter((candidate) => this.isCandidateAllowed(candidate, context));
    for (const candidate of candidates) {
      candidate.antiLoopPenalty = this.scorer.repetitionPenalty(candidate, context);
      candidate.utilityBreakdown = this.scorer.evaluate(candidate, context, candidate.antiLoopPenalty);
      candidate.utilityScore = candidate.utilityBreakdown.finalScore;
      candidate.staminaCost *= candidate.utilityBreakdown.staminaCostMultiplier;
      if (candidate.utilityBreakdown.debugReasons.length) {
        candidate.debugReason = `${candidate.debugReason} | ${candidate.utilityBreakdown.debugReasons.join('; ')}`;
      }
    }
    candidates.sort((a, b) => b.utilityScore - a.utilityScore || a.type.localeCompare(b.type) || (a.targetPlayerId ?? '').localeCompare(b.targetPlayerId ?? ''));
    const selection = this.selector.select(candidates, rng, context.state.config.utilityAi.selectionTemperature);
    return { context, candidates, chosen: selection.chosen, selection: selection.trace };
  }

  private isCandidateAllowed(candidate: ActionCandidate, context: PlayerDecisionContext): boolean {
    const actor = context.actor;
    if (actor.positionType === 'GK') {
      return actor.tacticalProfile.assignedRole === null
        && !['DRIBBLE', 'CARRY', 'SHOOT', 'TACKLE', 'INTERCEPT', 'PRESS', 'MARK_PLAYER', 'MAKE_RUN'].includes(candidate.type);
    }
    const role = actor.tacticalProfile.assignedRole;
    if (!validateAssignedRole(actor.positionType, role) || !role) return false;
    const definition = ROLE_DEFINITIONS[role];
    const emergency = context.minute >= 85 && context.scoreDiff < 0;
    if (role === 'CENTRAL' && candidate.type === 'SHOOT' && context.fieldZone !== 'FINAL' && !emergency) return false;
    if (!definition.constraints.canPressHigh && candidate.type === 'PRESS' && context.fieldZone === 'FINAL' && !emergency) return false;
    return this.roles.actionBias(role, candidate.type, candidate.passType).multiplier > 0;
  }

  private generateCandidates(context: PlayerDecisionContext): ActionCandidate[] {
    const actor = context.actor;
    if (!context.hasBall) {
      const owner = context.state.ball.ownerPlayerId
        ? context.opponents.find((player) => player.playerId === context.state.ball.ownerPlayerId)
        : undefined;
      if (owner && this.isDesignatedPresser(context, owner) && this.canChallengeOwner(context, owner) && actor.positionType !== 'GK') {
        const candidates = [this.candidate('TACKLE', actor, {
          targetPlayerId: owner.playerId,
          riskScore: 0.28,
          staminaCost: 1.1,
          debugReason: 'Poseedor rival dentro del radio de entrada',
        })];
        const techniqueCadenceReady = context.state.time - context.state.lastTechniqueTime[actor.teamSide]
          >= context.state.config.minimumTeamTechniqueIntervalSeconds
          && context.state.time - actor.lastTechniqueTime >= context.state.config.minimumPlayerTechniqueIntervalSeconds;
        for (const technique of techniqueCadenceReady ? actor.techniques : []) {
          if (technique.type !== 'BLOCK' || actor.tpCurrent < Math.max(technique.minTp, technique.tpCost)) continue;
          candidates.push(this.candidate('USE_TECHNIQUE', actor, {
            targetPlayerId: owner.playerId,
            targetPosition: owner.position,
            techniqueId: technique.id,
            tpCost: technique.tpCost,
            staminaCost: 1.4,
            debugReason: `${technique.name} disponible para disputar el balón`,
          }));
        }
        return candidates;
      }
      return [this.candidate('HOLD_POSITION', actor, { debugReason: 'Sin interacción inmediata con el balón' })];
    }

    if (context.state.pendingPenaltyTakerId === actor.playerId) {
      const shot = this.candidate('SHOOT', actor, {
        targetPosition: { x: PITCH_GEOMETRY.attackingGoalX(actor.teamSide), y: PITCH_GEOMETRY.centre.y },
        riskScore: 0.15,
        staminaCost: 1.4,
        debugReason: 'Lanzamiento de penalti obligatorio',
      });
      return [shot];
    }

    const candidates: ActionCandidate[] = [];
    const recentCross = actor.lastReceptionPassType === 'CROSS'
      && context.state.time - (actor.lastReceptionTime ?? -Infinity) <= context.state.config.crossFinishWindowSeconds;
    for (const teammate of context.teammates) {
      if (teammate.playerId === actor.playerId) continue;
      const targetPosition = this.predictedReceptionPoint(teammate, true);
      // A forward inside the final third must look for the goal first.  The
      // generic support map still leaves defenders behind the ball, but they
      // are an emergency outlet, not the natural destination of a striker in
      // the box.  Filtering these options before utility scoring avoids a
      // technically-safe but nonsensical "forward to centre-back" choice.
      if (!this.isSensiblePassTarget(context, teammate, targetPosition)) continue;
      // This must use the exact same line as the resolver. The former 0.75
      // tolerance made a runner who was already 0.1–0.7 units beyond the
      // line look legal to the passer, only to be flagged a moment later.
      // Also reserve a small timing margin for a runner accelerating while
      // the passer prepares the release.
      if (this.offsides.evaluate(context.state, actor, teammate, 0.05).offside) continue;
      if (!this.isPassTimingSafe(context, teammate)) continue;
      const passingLane = this.perception.assessPassingLane(actor, targetPosition, context.opponents);
      const passType = this.passType(actor, teammate, targetPosition, passingLane.blockers);
      candidates.push(this.candidate('PASS', actor, {
        targetPlayerId: teammate.playerId,
        targetPosition,
        passType,
        riskScore: passingLane.risk,
        passingLane,
        staminaCost: 0.65,
        debugReason: `Pase ${passType.toLowerCase()} a ${teammate.alias}; riesgo de línea ${passingLane.risk.toFixed(2)}`,
      }));
    }

    const dribbleDefender = context.opponents
      .filter((opponent) => distance(opponent.position, actor.position) <= 3.2)
      .sort((first, second) => distance(first.position, actor.position) - distance(second.position, actor.position) || first.playerId.localeCompare(second.playerId))[0];
    if (actor.positionType !== 'GK' && context.distanceToGoal > context.state.config.minimumDribbleGoalDistance && !recentCross) {
      const currentProgress = PITCH_GEOMETRY.relativeProgress(actor.teamSide, actor.position.x);
      // A striker may attack the last line without carrying the ball through
      // the goalkeeper.  The shooting-distance rule is also the hard ceiling
      // for an on-ball forward run, so the next decision is a finish or pass.
      const forwardCarryLimit = Math.min(
        context.state.config.maximumForwardRunProgress,
        100 - context.state.config.minimumDribbleGoalDistance,
      );
      // A short three-metre shuffle is not a meaningful attacking action.
      // Midfielders get enough runway to visibly break the first line when
      // they decide to conduct, while forwards still stop at shooting range.
      const carryDistance = actor.positionType === 'MF' ? 12 : 9;
      const targetProgress = actor.positionType === 'FW'
        ? Math.min(currentProgress + carryDistance, forwardCarryLimit)
        : currentProgress + carryDistance;
      const wideCarrier = actor.tacticalProfile.assignedRole === 'WINGER'
        || actor.tacticalProfile.assignedRole === 'LATERAL'
        || Math.abs(actor.homePosition.y - PITCH_GEOMETRY.centre.y) >= 27;
      // A winger who receives wide must attack the channel, not be pulled
      // mechanically back to the centre every time they carry. Centrally
      // based players may still angle their run a little towards goal.
      const targetY = wideCarrier
        ? actor.position.y
        : actor.position.y + (PITCH_GEOMETRY.centre.y - actor.position.y) * 0.12;
      candidates.push(this.candidate(dribbleDefender ? 'DRIBBLE' : 'CARRY', actor, {
        targetPosition: PITCH_GEOMETRY.clampRestart({
          x: PITCH_GEOMETRY.worldX(actor.teamSide, targetProgress),
          y: targetY,
        }),
        riskScore: context.pressureLevel,
        staminaCost: 1.25,
        debugReason: dribbleDefender
          ? `Regate ante ${dribbleDefender.alias} a ${distance(actor.position, dribbleDefender.position).toFixed(1)} unidades`
          : `Conducción ofensiva hacia espacio libre con presión ${context.pressureLevel.toFixed(2)}`,
      }));
    }
    const shotPatience = context.minute >= 80 && context.scoreDiff < 0
      ? context.state.config.minimumTeamShotIntervalSeconds * 0.5
      : context.state.config.minimumTeamShotIntervalSeconds;
    const shootingRange = actor.positionType === 'FW' ? 36
      : actor.positionType === 'MF' ? 32
        : actor.positionType === 'DF' ? 25 : 0;
    const clearGoalChance = this.hasClearGoalChance(context);
    const stalledFinalThirdAttack = this.hasStalledFinalThirdAttack(context, shootingRange);
    const finishOpportunity = context.distanceToGoal <= context.state.config.closeRangeShotDistance
      || recentCross || clearGoalChance || stalledFinalThirdAttack;
    const shotCadenceReady = context.state.time - context.state.lastShotTime[actor.teamSide] >= shotPatience;
    const finishingCadenceReady = context.state.time - context.state.lastShotTime[actor.teamSide]
      >= context.state.config.minimumFinishingShotIntervalSeconds;
    // The team-wide shot cooldown is useful to avoid arcade shooting from
    // midfield.  It must never suppress a clear chance or allow an attack to
    // pass itself out of the box after several non-finishing touches.
    const mustFinish = finishOpportunity && (finishingCadenceReady
      || context.distanceToGoal <= 7 || clearGoalChance || stalledFinalThirdAttack);
    if (context.distanceToGoal <= shootingRange && (shotCadenceReady || mustFinish)) {
      candidates.push(this.candidate('SHOOT', actor, {
        targetPosition: { x: PITCH_GEOMETRY.attackingGoalX(actor.teamSide), y: PITCH_GEOMETRY.centre.y },
        riskScore: clamp(context.distanceToGoal / 65, 0, 1),
        staminaCost: 1.4,
        debugReason: recentCross
          ? 'Finalización prioritaria tras recibir un centro'
          : clearGoalChance
            ? `Ocasión clara a ${context.distanceToGoal.toFixed(1)} unidades: finalizar`
            : stalledFinalThirdAttack
              ? `Ataque estancado tras ${context.consecutiveTeamPasses} pases: finalizar`
          : `Tiro disponible a ${context.distanceToGoal.toFixed(1)} unidades`,
      }));
    }
    if (context.fieldZone === 'DEFENSIVE' && context.pressureLevel >= context.state.config.emergencyClearPressure) {
      const outlet = this.clearanceOutlet(context);
      const targetPosition = outlet ? this.predictedReceptionPoint(outlet) : { x: actor.teamSide === 0 ? 64 : 36, y: actor.position.y };
      const passingLane = this.perception.assessPassingLane(actor, targetPosition, context.opponents);
      candidates.push(this.candidate('CLEAR_BALL', actor, {
        targetPlayerId: outlet?.playerId,
        targetPosition,
        passType: outlet && distance(actor.position, targetPosition) < 24 ? 'DRIVEN' : 'LOB',
        passingLane,
        riskScore: passingLane.risk,
        staminaCost: 1,
        debugReason: outlet
          ? `Despeje dirigido hacia ${outlet.alias}`
          : 'Despeje de emergencia sin una salida segura',
      }));
    }

    const techniqueCadenceReady = context.state.time - context.state.lastTechniqueTime[actor.teamSide]
      >= context.state.config.minimumTeamTechniqueIntervalSeconds
      && context.state.time - actor.lastTechniqueTime >= context.state.config.minimumPlayerTechniqueIntervalSeconds;
    for (const technique of techniqueCadenceReady ? actor.techniques : []) {
      if (actor.tpCurrent < Math.max(technique.minTp, technique.tpCost)) continue;
      // A special shot enhances a coherent chance; it must not bypass the
      // collective shooting cadence and turn every attacker into a looping
      // technique cannon as soon as they regain TP.
      const compatible = (technique.type === 'SHOT' && context.distanceToGoal <= shootingRange + 8
        && (shotCadenceReady || mustFinish))
        || technique.type === 'PASS'
        || (technique.type === 'DRIBBLE' && actor.positionType !== 'GK' && !mustFinish && Boolean(dribbleDefender))
        || technique.type === 'BUFF';
      if (!compatible) continue;
      const baseTarget = technique.type === 'SHOT'
        ? { x: PITCH_GEOMETRY.attackingGoalX(actor.teamSide), y: PITCH_GEOMETRY.centre.y }
        : candidates.find((candidate) => candidate.type === technique.type)?.targetPosition
          ?? candidates.find((candidate) => candidate.type === 'PASS')?.targetPosition
          ?? actor.targetPosition;
      const passCandidate = technique.type === 'PASS' ? candidates.find((candidate) => candidate.type === 'PASS') : undefined;
      const passTarget = passCandidate?.targetPlayerId;
      candidates.push(this.candidate('USE_TECHNIQUE', actor, {
        targetPlayerId: passTarget,
        targetPosition: baseTarget,
        techniqueId: technique.id,
        passType: passCandidate?.passType,
        tpCost: technique.tpCost,
        staminaCost: 1.5,
        debugReason: `${technique.name} disponible con ${actor.tpCurrent.toFixed(0)} TP`,
      }));
    }

    candidates.push(this.candidate('HOLD_POSITION', actor, { debugReason: 'Conservar posesión y esperar apoyos' }));
    return candidates;
  }

  private canChallengeOwner(context: PlayerDecisionContext, owner: PlayerRuntimeState): boolean {
    const defender = context.actor;
    const separation = distance(defender.position, owner.position);
    if (separation > 1.85 || (defender.bypassedUntil ?? -Infinity) > context.state.time) return false;
    // A defender who is already trailing the ball carrier cannot make a
    // long, invisible tackle through their back. Close shoulder contact is
    // still legal, but anything further behind must become a recovery run.
    const trailingDistance = (owner.position.x - defender.position.x) * (owner.teamSide === 0 ? 1 : -1);
    return trailingDistance <= .45 || separation <= 1.15;
  }

  private isFinishingCandidate(candidate: ActionCandidate, actor: PlayerRuntimeState): boolean {
    if (candidate.type === 'SHOOT') return true;
    if (candidate.type !== 'USE_TECHNIQUE') return false;
    return actor.techniques.find((technique) => technique.id === candidate.techniqueId)?.type === 'SHOT';
  }

  /**
   * A clear chance is deliberately stricter than merely being in the final
   * third.  It requires a usable angle, a short-ish distance and no defensive
   * wall on the shooting line.  That preserves sensible reset passes when a
   * striker is genuinely boxed in, while preventing the seven-pass sequence
   * visible in the match recording when the goal is open.
   */
  private hasClearGoalChance(context: PlayerDecisionContext): boolean {
    const actor = context.actor;
    if (context.fieldZone !== 'FINAL' || actor.positionType === 'GK') return false;
    const maximumDistance = actor.positionType === 'FW' ? 22 : actor.positionType === 'MF' ? 18 : 0;
    if (context.distanceToGoal > maximumDistance || context.perception.goalAngleQuality < .58) return false;

    const goal = { x: PITCH_GEOMETRY.attackingGoalX(actor.teamSide), y: PITCH_GEOMETRY.centre.y };
    const lineX = goal.x - actor.position.x;
    const lineY = goal.y - actor.position.y;
    const lineLengthSquared = lineX * lineX + lineY * lineY || 1;
    const defenders = context.opponents.filter((opponent) => opponent.positionType !== 'GK');
    const laneBlockers = defenders.filter((opponent) => {
      const projection = ((opponent.position.x - actor.position.x) * lineX
        + (opponent.position.y - actor.position.y) * lineY) / lineLengthSquared;
      if (projection <= .08 || projection >= .94) return false;
      const closestX = actor.position.x + lineX * projection;
      const closestY = actor.position.y + lineY * projection;
      return Math.hypot(opponent.position.x - closestX, opponent.position.y - closestY) < 4.2;
    });
    const immediateDefenders = defenders.filter((opponent) => distance(opponent.position, actor.position) <= 6.5);
    return laneBlockers.length === 0 && immediateDefenders.length <= 1 && context.pressureLevel < .72;
  }

  private hasStalledFinalThirdAttack(context: PlayerDecisionContext, shootingRange: number): boolean {
    if (context.fieldZone !== 'FINAL' || context.distanceToGoal > shootingRange) return false;
    if (context.consecutiveTeamPasses < 3 || context.perception.goalAngleQuality < .46) return false;
    const nearbyDefenders = context.opponents
      .filter((opponent) => opponent.positionType !== 'GK')
      .filter((opponent) => distance(opponent.position, context.actor.position) <= 6.5);
    // A shot is still preferable to an endless loop with a single marker;
    // two close defenders is a real blocked lane and remains a reset/dribble.
    return nearbyDefenders.length <= 1;
  }

  /** DecisionSystem is also used by debugging and tests outside MatchEngine.
   * Keep the one-presser rule here as well, otherwise every nearby defender
   * can independently request the same tackle during a decision window. */
  private isDesignatedPresser(context: PlayerDecisionContext, owner: PlayerRuntimeState): boolean {
    const nearest = context.state.players
      .filter((player) => player.teamSide === context.actor.teamSide)
      .filter((player) => player.positionType !== 'GK')
      .sort((first, second) => distance(first.position, owner.position) - distance(second.position, owner.position)
        || first.playerId.localeCompare(second.playerId))[0];
    return nearest?.playerId === context.actor.playerId;
  }

  private context(state: MatchState, actor: PlayerRuntimeState): PlayerDecisionContext {
    const perception = this.perception.observe(state, actor);
    const progress = PITCH_GEOMETRY.relativeProgress(actor.teamSide, actor.position.x);
    const possessionSequence = this.possessionSequence(state, actor.teamSide, progress);
    return {
      state,
      actor,
      teammates: perception.teammates,
      opponents: perception.opponents,
      perception: perception.actor,
      teamPlan: state.teamAi[actor.teamSide],
      hasBall: state.ball.ownerPlayerId === actor.playerId,
      fieldZone: PITCH_GEOMETRY.classifyThird(actor.teamSide, actor.position.x),
      scoreDiff: state.score[actor.teamSide] - state.score[actor.teamSide === 0 ? 1 : 0],
      pressureLevel: perception.actor.pressure,
      distanceToGoal: perception.actor.distanceToGoal,
      minute: state.time / 60,
      momentum: state.momentum[actor.teamSide],
      consecutiveTeamPasses: possessionSequence.passCount,
      recentTerritorialProgress: possessionSequence.progress,
      sequenceStartProgress: possessionSequence.startProgress,
    };
  }

  private possessionSequence(state: MatchState, side: 0 | 1, currentProgress: number): { passCount: number; progress: number; startProgress: number } {
    const history = state.recentActions[side];
    let firstPassIndex = history.length;
    while (firstPassIndex > 0 && history[firstPassIndex - 1].type === 'PASS') firstPassIndex--;
    const consecutivePasses = history.slice(firstPassIndex);
    const window = consecutivePasses.slice(-state.config.stagnationPassLimit);
    if (!window.length) return { passCount: 0, progress: 0, startProgress: currentProgress };
    const start = window[0].startProgress ?? currentProgress;
    const best = window.reduce((value, action) => Math.max(value, action.targetProgress ?? value), start);
    return { passCount: consecutivePasses.length, progress: Math.max(0, best - start), startProgress: start };
  }

  private isSensiblePassTarget(
    context: PlayerDecisionContext,
    teammate: PlayerRuntimeState,
    target: Vector2,
  ): boolean {
    const direction = context.actor.teamSide === 0 ? 1 : -1;
    const forwardGain = (target.x - context.actor.position.x) * direction;
    const passDistance = distance(context.actor.position, target);
    const actorIsForward = context.actor.positionType === 'FW';
    const actorIsGoalkeeper = context.actor.positionType === 'GK';
    const finalThird = context.fieldZone === 'FINAL';
    const underHeavyPressure = context.pressureLevel >= 0.68;

    // A standard pass is a link between nearby lanes, not a teleport across
    // the full pitch. Clearances remain available as their own action when a
    // team is genuinely under pressure.
    const purposefulRun = teammate.movementState === 'MAKING_RUN' && forwardGain >= 8;
    if (passDistance > (purposefulRun ? 56 : underHeavyPressure ? 48 : 40)) return false;

    // Build-up begins with defenders and midfielders. A goalkeeper launching
    // repeated direct balls to a forward skips the whole tactical shape and
    // produces the visibly unnatural GK→FW loop seen in live matches.
    if (actorIsGoalkeeper) {
      if (teammate.positionType === 'GK' || teammate.positionType === 'FW') return false;
      const maximumDistribution = underHeavyPressure ? 40 : 30;
      if (passDistance > maximumDistribution || forwardGain > maximumDistribution) return false;
    }

    // Centre-backs can switch play, but they should not turn every recovery
    // into an instant long ball to the striker while safer midfield outlets
    // are on the pitch.
    if (context.fieldZone === 'DEFENSIVE' && context.actor.positionType === 'DF'
      && teammate.positionType === 'FW' && this.hasBuildUpOutlet(context)) return false;

    // Never route an established attack back to the goalkeeper without a
    // credible pressing threat. It creates long possession loops with no
    // footballing purpose.
    if (finalThird && teammate.positionType === 'GK' && !underHeavyPressure) return false;

    // This is the visible case players reported: a striker in a shooting area
    // choosing an unmarked defender far behind them. Under genuine pressure it
    // remains available as a safety valve, otherwise it is not offered.
    if (actorIsForward && finalThird && teammate.positionType === 'DF'
      && forwardGain < 5 && !underHeavyPressure) return false;

    // In a credible shooting zone, a striker may recycle to a nearby support
    // player, but not reset the whole attack to a defensive line. This leaves
    // room for a shot, a dribble or a combination around the area.
    if (actorIsForward && context.distanceToGoal <= 28 && teammate.positionType === 'DF'
      && !underHeavyPressure) return false;
    if (actorIsForward && context.distanceToGoal <= 22 && teammate.positionType === 'MF'
      && forwardGain < -3 && !underHeavyPressure && teammate.intent.reason !== 'RECYCLE_ATTACK') return false;

    // Deep backward options in the final third amplify circular exchanges.
    // Keep them only when pressure makes retaining possession more sensible
    // than forcing an action.
    if (finalThird && forwardGain < -10 && !underHeavyPressure && teammate.intent.reason !== 'RECYCLE_ATTACK') return false;
    return true;
  }

  private hasBuildUpOutlet(context: PlayerDecisionContext): boolean {
    return context.teammates.some((teammate) => teammate.playerId !== context.actor.playerId
      && teammate.positionType !== 'GK' && teammate.positionType !== 'FW'
      && distance(context.actor.position, teammate.position) <= 34);
  }

  /** Finds a nearby line-breaking outlet before conceding a 50/50 long ball. */
  private clearanceOutlet(context: PlayerDecisionContext): PlayerRuntimeState | undefined {
    const direction = context.actor.teamSide === 0 ? 1 : -1;
    return context.teammates
      .filter((teammate) => teammate.playerId !== context.actor.playerId && teammate.positionType !== 'GK'
        && distance(context.actor.position, teammate.position) <= 35)
      .map((teammate) => {
        const target = this.predictedReceptionPoint(teammate);
        const lane = this.perception.assessPassingLane(context.actor, target, context.opponents);
        const gain = (target.x - context.actor.position.x) * direction;
        const linkBonus = teammate.positionType === 'MF' ? 30 : teammate.positionType === 'DF' ? 18 : 0;
        return { teammate, score: linkBonus + clamp(gain, -5, 18) - lane.risk * 35 };
      })
      .filter((option) => option.score > 0)
      .sort((a, b) => b.score - a.score || a.teammate.playerId.localeCompare(b.teammate.playerId))[0]?.teammate;
  }

  private predictedReceptionPoint(player: PlayerRuntimeState, leadRun = false): Vector2 {
    if (player.intent.reason === 'RECYCLE_ATTACK') {
      const target = player.intent.idealTarget;
      return PITCH_GEOMETRY.clampPrediction({
        x: player.position.x + (target.x - player.position.x) * 0.48,
        y: player.position.y + (target.y - player.position.y) * 0.48,
      });
    }
    if (leadRun && player.movementState === 'MAKING_RUN') {
      const runTarget = player.intent.idealTarget;
      return PITCH_GEOMETRY.clampPrediction({
        x: player.position.x + (runTarget.x - player.position.x) * 0.58,
        y: player.position.y + (runTarget.y - player.position.y) * 0.58,
      });
    }
    return PITCH_GEOMETRY.clampPrediction({
      x: player.position.x + player.velocity.x * 1.2,
      y: player.position.y + player.velocity.y * 1.2,
    });
  }

  /**
   * Passing decisions are taken a fraction before the ball is released. A
   * receiver who is technically onside now but sprinting through a high line
   * is therefore not treated as a free outlet. This is deliberately a small
   * forecast: it protects the release instant, not the legal run after it.
   */
  private isPassTimingSafe(context: PlayerDecisionContext, receiver: PlayerRuntimeState): boolean {
    const setupSeconds = receiver.movementState === 'MAKING_RUN' ? .38 : .18;
    const progress = (worldX: number) => PITCH_GEOMETRY.relativeProgress(context.actor.teamSide, worldX);
    const secondLastOpponent = context.opponents
      .map((opponent) => progress(opponent.position.x + opponent.velocity.x * setupSeconds))
      .sort((first, second) => second - first)[1]
      ?? PITCH_GEOMETRY.length;
    const receiverProgress = progress(receiver.position.x + receiver.velocity.x * setupSeconds);
    const ballProgress = progress(context.state.ball.position.x + context.actor.velocity.x * .12);
    const offsideLine = Math.max(ballProgress, secondLastOpponent);
    const safetyMargin = receiver.movementState === 'MAKING_RUN' ? .7 : .28;
    return receiverProgress <= offsideLine - safetyMargin;
  }

  private passType(actor: PlayerRuntimeState, receiver: PlayerRuntimeState, target: Vector2, blockers: number): PassType {
    const passDistance = distance(actor.position, target);
    const actorProgress = PITCH_GEOMETRY.relativeProgress(actor.teamSide, actor.position.x);
    const receiverProgress = PITCH_GEOMETRY.relativeProgress(actor.teamSide, receiver.position.x);
    const wideDelivery = (actor.position.y <= 24 || actor.position.y >= 76)
      && actorProgress >= 62 && receiverProgress >= 78
      && receiver.position.y >= PITCH_GEOMETRY.penaltyArea.minY
      && receiver.position.y <= PITCH_GEOMETRY.penaltyArea.maxY;
    if (wideDelivery) return 'CROSS';
    const forwardGain = (target.x - actor.position.x) * (actor.teamSide === 0 ? 1 : -1);
    if (receiver.movementState === 'MAKING_RUN' && forwardGain >= 8 && passDistance >= 12) return 'THROUGH';
    if (actor.positionType === 'GK' || (blockers >= 2 && passDistance >= 14)) return 'LOB';
    if (passDistance >= 22) return 'DRIVEN';
    return 'GROUND';
  }

  private candidate(type: ActionCandidate['type'], actor: PlayerRuntimeState, values: Partial<ActionCandidate>): ActionCandidate {
    return {
      type,
      actorId: actor.playerId,
      utilityScore: 0,
      riskScore: 0,
      staminaCost: 0,
      tpCost: 0,
      debugReason: '',
      ...values,
    };
  }
}
